import { Money } from '@libs/money/money.interfaces';
import { AdditionalBaggage } from '@shared/interfaces/baggage';
import { AnyProduct } from '@shared/interfaces/product';
import { FerryVehicle } from '@shared/interfaces/ferry';
import { CartProductCombination } from '@shared/interfaces/productCombination';
import { TransferJourneyType } from '@/helpers/transfer/transfer.interfaces';
import {
    FlightBrandedAttributeStatus,
    FlightBrandedAttributeType,
} from '@shared/interfaces/brandedFares';

export interface CartEndpoint {
    GetCart(bookingToken: string | null): Promise<Cart>;
    UpdateCart(
        cart: Cart,
        origin: BookingOrigin,
        moveToNextStage: boolean,
        validateMandatory?: boolean
    ): Promise<Cart>;
}

type BonusType = 'BonusNight';

export enum SalesChannel {
    Unknown = 'Unknown',
    Affiliate = 'Affiliate',
    B2B = 'B2B',
    Direct = 'Direct',
    Meta_flight = 'Meta_flight',
    Meta_hotel = 'Meta_hotel',
    Meta_package = 'Meta_package',
    Mailing = 'Mailing',
    Paid_advertising = 'Paid_advertising',
    Paid_search = 'Paid_search',
    Partner = 'Partner',
    Retail = 'Retail',
    Share_Gain = 'Share_Gain',
    SoMe = 'SoMe',
    Stena_Line = 'Stena_Line',
    Test = 'Test',
    Travel_agency = 'Travel_agency',
    WhiteLabel = 'WhiteLabel',
    Groups = 'Groups',
}

export enum BusinessArea {
    Unknown = 'Unknown',
    Sembo = 'Sembo',
    EOF = 'EOF',
    Nordbeds = 'Nordbeds',
}

export type BookingSalesChannel = string;

export interface Bonus {
    Type: BonusType;
    Value: Money;
    IsApplied: boolean;
    Id: string;
}

export interface Cart extends CartExtras {
    Agency?: Agency | null;
    ShoppingCartKey: string;
    BookingToken: string | null;
    Customer: Customer;
    Products: AnyProduct[];
    AvailableAddons: AddonProduct[];
    Payment: Payment;
    AvailableBonus: Bonus[];
    //AppliedBonusNights: AppliedBonusNights[]; // Property exists on BE
    NextUrl: string;
    ErrorMessage: string | null;
    FlowContainsPreCheckout: boolean;
    Affiliate: CartAffiliate | null;
    EstimatedMargin: Money | null;
    BookingSalesChannels: BookingSalesChannel[];
    FlowContainsPostCheckout: boolean;
    ExpirationInSeconds?: number;
    RedirectUrlOnExpiration?: string;
}

export interface CartAffiliate {
    AffiliateId: string | null;
    SalesChannel: SalesChannel;
    BusinessArea: BusinessArea;
    GroupName: string | null;
}

export enum CartPaymentStatus {
    Unpaid = 'Unpaid',
    PartiallyPaid = 'PartiallyPaid',
    Paid = 'Paid',
}

interface CartExtras {
    ProductCombination: CartProductCombination | null;
}

export interface Agency {
    AgencyNumber: string | null;
    Reference: string | null;
    Email: string | null;
    Phone: string | null;
    Commission: Money | null;
}
export interface Customer {
    PhoneNumber: string | null;
    CountryCode: string;
    Email: string | null;
    ReceiveNewsletter: boolean;
    IsLoggedIn: boolean;
}

export interface Payment {
    PaymentId: string | null;
    PaymentDiscount: Money | null;
    DiscountedPaymentMethods: string[];
    PaymentMethods: Array<PaymentMethod | LeaBankPaymentMethod | AdyenPaymentMethod>;
    SelectedPaymentOption: PaymentMethod | LeaBankPaymentMethod | AdyenPaymentMethod | null;
    SelectedPartPaymentOption: PartPaymentMethodType;
    GiftCards: GiftCard[];
    AmountAlreadyPaid: Money;
    CampaignDiscount: Money | null;
    PaymentDueTime: string | null;
    PartPaymentOptions: PartPaymentOption[];
}

export interface Product extends ProductExtra {
    $type: SerializationType;
    Id: string;
    Type: ProductType;
    Tags: Tag[];
    Description: string;
    Price: Money; // Applied has the discount...
    AppliedDiscount: AppliedDiscount | null;
}

export interface AppliedDiscount {
    Name: string;
    Amount: Money;
    IsApplied?: boolean;
}

interface ProductExtra {
    IsBooked?: boolean;
    IsPaid?: boolean;
    Updating?: boolean;
}

export enum ProductType {
    Hotel = 'Hotel',
    Flight = 'Flight',
    Ferry = 'Ferry',
    Activity = 'Activity',
    Transfer = 'Transfer',
    Addon = 'Addon',
    Giftcard = 'Giftcard',
    Discount = 'Discount',
    Ticket = 'Ticket',
    Other = 'Other',
    Train = 'Train',
    Golf = 'Golf',
    FerryAddons = 'FerryAddons',
}

export enum SerializationType {
    ActivityProduct = 'ActivityProduct',
    FlightProduct = 'FlightProduct',
    FerryProduct = 'FerryProduct',
    HotelProduct = 'HotelProduct',
    TransferProduct = 'TransferProduct',
    ServicePackage = 'ServicePackage',
    LuggageInsurance = 'LuggageInsurance',
    CancellationInsurance = 'CancellationInsurance',
    TravelInsurance = 'TravelInsurance',
    FlightConfirmationSms = 'FlightConfirmationSms',
    Sembonus = 'Discount',
}

type Tag =
    | 'ShowOnCheckoutPage'
    | 'ShowOnPaymentPage'
    | 'CancellationInsurance'
    | 'ServicePackage'
    | 'LuggageInsurance'
    | 'Seating'
    | 'TravelInsurance';

export type AddonType =
    | 'CancellationInsurance'
    | 'ServicePackage'
    | 'LuggageInsurance'
    | 'TravelInsurance'
    | 'FlightConfirmationSms';

export interface GiftCard {
    Code: string;
    Value: Money;
    PaymentId?: string;
}

export interface AddonProduct extends Product, AddonProductExtra {
    LinkUrl: string | null; //TODO: BE could return string[] to provide LuggageInsurance links
    SpecialRequest?: string | null;
    IsInsurance?: boolean;
    ShowDiscardPopUp: boolean;
}

export interface ServicePackageAddon extends AddonProduct {
    Ordinal: number;
    OriginalPrice?: Money | null;
}

interface AddonProductExtra {
    LinkUrlSecondary?: string | null;
}

export interface FerryAddonsProduct extends Product {
    Addons: Product[];
}

export interface FlightProduct extends Product {
    CombinationId?: string | null;
    Pax: FlightPax[];
    Legs: FlightLeg[];
    DiscountedPrice: Money;
    PriceBreakdown: FlightPriceBreakdown | null;
    FlightTermsUrl: string | null;
    Seating: Seating | null;
    Gordian: GordianSeating | null;
    IsRebookable: boolean;
    BrandedFare?: FlightBrandedFare | null;
}

export interface FlightBrandedFare {
    BrandName: string;
    Attributes: FlightBrandedFareAttribute[];
}

export interface FlightBrandedFareAttribute {
    Type: FlightBrandedAttributeType;
    Status: FlightBrandedAttributeStatus;
}

export interface GordianSeating {
    TripId: string | null;
    TripAccessToken: string | null;
    SearchId: string | null;
    SeatAvailability: SeatAvailability | null;
    WidgetStatus?: GordianWidgetStatus | null;
    Active?: boolean | null;
}

export enum GordianWidgetStatus {
    CREATE = 'CREATE',
    ERROR = 'ERROR',
    INITIAL = 'INITIAL',
    LOADED = 'LOADED',
    LOADING = 'LOADING',
    UPDATING = 'UPDATING',
}

export interface FerryProduct extends Product {
    CombinationId?: string | null;
    Pax: FerryPax[];
    Legs: FerryLeg[];
    DiscountedPrice: Money;
}

export interface Seating {
    SeatMapIdentifier: string;
    Price: {
        Amount: number;
        Currency: string;
    };
}

export interface FlightPriceBreakdown {
    FareTypes: FlightPriceBreakdownByFareType[];
    RebookableFee: Money | null;
}

export interface FlightPriceBreakdownByFareType {
    FareType: FarePassengerType;
    NumberOfPax: number;
    FlightPrice: Money | null;
    TaxesAndFees: Money | null;
    BookingFee: Money | null;
    TotalPrice: Money;
}

export enum FarePassengerType {
    Adult = 1,
    Child = 2,
    Infant = 3,
    Youth = 4,
}

export interface TransferProduct extends TransferProductExtras, Product {
    OfferId: string;
    FlightNumber: string | null;
    Passengers: TransferPassengers;
    WheelChairsPlaces: number;
    AnimalsPlaces: number;
    MainPassenger: TransferMainPassenger | null;
    Comment: string | null;
    PickUpTime: string | null;
    ShieldText: string | null;
    From: TransferLocation;
    To: TransferLocation;
    Luggage: number;
    Distance: number;
    Duration: number;
    TollsIncluded: boolean;
    GratuityIncluded: boolean;
    DriverWaitingTime: number;
    FreeCancelUntil: Date | null;
    Vehicle: TransferVehicle;
    Partner: TransferPartner;
    TransferType: string; //Should be the same option TransferType enum?
    CancellationPolicyText: string | null;
    Extras: TransferExtra[] | null;
    JourneyType: TransferJourneyType;
    DisclaimerTitle: string | null;
    DisclaimerDescription: string | null;
    SupplierPrice?: Money | null;
    IsSharedTransfer: boolean;
}

interface TransferPassengers {
    Adults: number;
    Children: number;
    Infants: number;
}

export interface TransferMainPassenger {
    FirstName: string;
    LastName: string;
    MiddleName?: string;
    Phone: string;
    Email: string;
}

interface TransferProductExtras {
    TripId?: string;
}

export interface TransferVehicle {
    Image: string;
    Model: string;
    ServiceType: string; // Enum in the future?
    TransferCategory: string; // Enum in the future?
}
export interface TransferPartner {
    Name: string;
    Image: string;
}

export interface TransferLocation {
    Iata?: string;
    Type?: string;
    Address?: string;
    Coordinates?: TransferLocationCoordinates;
    HotelName?: string;
}

interface TransferLocationCoordinates {
    Latitude?: number;
    Longitude?: number;
}

export interface TransferExtra {
    Id: string;
    Count: number;
}

export interface HotelProduct extends Product {
    CombinationId?: string | null;
    HotelCode: string;
    IsRefundable: boolean | null;
    Destination: string;
    ImageUrl: string;
    FromDate: string;
    ToDate: string;
    Rating: number | null;
    PolygonId: number | null;
    Address: HotelAddress | null;
    Position: GeoLocation | null;
    Rooms: Room[];
}

export interface HotelAddress {
    City: string | null;
    Country: string | null;
    StreetAddress: string | null;
    ZipCode: string | null;
}

export interface GeoLocation {
    Latitude: number | null;
    Longitude: number | null;
}

export interface BonusNightProduct extends Product {
    AppliedBonusNights: AppliedBonusNight[];
}

interface AppliedBonusNight {
    Value: Money;
}

export interface ActivityProduct extends Product {
    Address: string | null;
    CombinationId?: string | null;
    PlaceCode: string;
    IsRefundable: boolean | null;
    ImageUrl: string;
    Rating: number | null;
    Date: string;
    Tickets: ActivityTicket[];
    PolygonId: number | null;
    Location: GeoLocation | null;
}
export interface ActivityTicket {
    Id: string;
    Description: string;
    CustomerCancellationPolicies: CancellationPolicy[];
    Pax: ActivityTicketPax;
    Price: Money;
    CategoryDescription: string | null;
}
export interface ActivityTicketPax extends Omit<Pax, 'Gender'> {
    PersonalIdNumber: string | null;
    Email: string | null;
    Phone: string | null;
    Gender: string | null;
    Organization: string | null;
    Address: string | null;
    Language: string | null;
    TicketFields: ActivityTicketSpecificField[];
}
export interface ActivityTicketSpecificField {
    Id: string;
    Question: string;
    Placeholder: string;
    Answer: string[];
    ExpectedAnswerType: BookingQuestionAnswerType;
    AnswerOptions: BookingQuestionOption[];
    CanSelectMultipleOptions: boolean;
}

interface BookingQuestionOption {
    Id: string;
    Value: string;
}

export enum BookingQuestionAnswerType {
    ShortText,
    LongText,
    Integer,
    Decimal,
    Boolean,
    Date,
    DateAndTime,
    ListOfValues,
    ListOfValuesOrText,
}

//Mandatory Fields for Activity tickets
export enum MandatoryFieldId {
    TITLE = 'TITLE',
    FIRST_NAME = 'FIRST_NAME',
    LAST_NAME = 'LAST_NAME',
    PERSONAL_ID_NUMBER = 'PERSONAL_ID_NUMBER',
    EMAIL = 'EMAIL',
    PHONE_NUMBER = 'PHONE_NUMBER',
    NATIONALITY = 'NATIONALITY',
    GENDER = 'GENDER',
    ORGANIZATION = 'ORGANIZATION',
    PASSPORT_ID = 'PASSPORT_ID',
    PASSPORT_EXPIRY = 'PASSPORT_EXPIRY',
    ADDRESS = 'ADDRESS',
    DATE_OF_BIRTH = 'DATE_OF_BIRTH',
    LANGUAGE = 'LANGUAGE',
}

export enum Gender {
    MALE = 'Male',
    FEMALE = 'Female',
    OTHER = 'Other',
}

export interface Room {
    Id: string;
    Description: string;
    Pax: HotelPax;
    PaxDescription: PaxDescription[];
    Price: Money;
    PriceAtLocation: Money | null;
    PriceExcludingTaxAndFees: Money | null;
    TaxesAndFees: TaxOrFee[];
    CustomerCancellationPolicies: CancellationPolicy[];
    AllNotes: Note[];
}

export interface Note {
    Scope: NoteScope;
    Text: string;
}

type NoteScope = 'Hotel' | 'Room';

export interface Pax extends PaxExtra {
    Id?: string | null;
    Title: number | null; //Deprecated: we receive it but use Gender instead
    Gender: number | null;
    FirstName: string | null;
    LastName: string | null;
    DateOfBirth: string | null;
    MandatoryFields: string[];
    Nationality: Nationality | null;
    PassportNumber?: string | null;
    PassportExpirationDate?: string | null;
    SpecialNeedsOption: number | null;
}

interface PaxExtra {
    Error?: string | null;
}

export interface HotelPax extends Pax {
    MandatoryFields: MandatoryField[];
    SpecialRequest: string | null;
}

export interface FlightPax extends Pax {
    MandatoryFields: MandatoryField[];
    PassengerType?: FarePassengerType | null;
    PaxDescription: PaxDescription | null;
    AdditionalBaggage: AdditionalBaggage;
    SelectedSeats?: SeatingProduct[] | null;
}

export interface SeatingProduct {
    Description: string;
    Price: Money;
    Row: number;
    Column: string;
    Id: string;
    PriceBreakdown: SeatingPriceBreakdown | null;
}

interface SeatingPriceBreakdown {
    SupplierPrice: Money;
    BookingFee: Money;
    TotalPrice: Money;
}

export enum SeatAvailability {
    Unknown = 'Unknown',
    // Last check search was still in progress
    InProgress = 'InProgress',
    // Seats can be selected for this trip
    Available = 'Available',
    // Search has completed and no seats was available to be selected for this trip
    NotAvailable = 'NotAvailable',
}

export interface FerryPax extends Pax {
    MandatoryFields: MandatoryField[];
    PaxDescription: PaxDescription | null;
    LoyaltyMembership?: string | null;
}

export interface PaxDescription {
    IsAdult: boolean;
    Age: number | null;
}

export interface Nationality {
    CountryCode: string;
    NationalityName: string;
}

export interface FlightLeg {
    Segments: FlightSegment[];
    IncludedBaggage: IncludedBaggage;
    PlatingCarrier: string | null;
    PlatingCarrierLogo: string | null;
    PlatingCarrierName: string | null;
    TravelTime: string | null;
}

export interface IncludedBaggage {
    Cabin: IncludedBaggageInfo | null;
    CheckedIn: IncludedBaggageInfo | null;
}

interface IncludedBaggageInfo {
    Pieces: null | number;
    WeightPerPiece: null | number;
    TotalWeight: null | number;
    LimitedInfo: null | boolean;
    AtLeastOneBagIncluded: boolean;
}

export interface FerryLeg {
    Segments: FerrySegment[];
}

interface Segment {
    CompanyLogo: string | null;
    DepartureDate: string;
    ArrivalDate: string;
}

export interface FlightSegment extends Segment {
    ArrivalAirportCode: string;
    ArrivalAirportName: string | null;
    ArrivalLocationName: string | null;
    ArrivalAirportLabel: string | null;
    CabinClass: CabinClass;
    DepartureAirportCode: string;
    DepartureAirportName: string | null;
    DepartureLocationName: string | null;
    DepartureAirportLabel: string | null;
    FlightNumber: string;
    FlightTime: string | null;
    HasTechnicalStop: boolean | null;
    MarketingCarrier: string;
    MarketingCarrierLogoUrl: string | null;
    MarketingCarrierName: string | null;
    OperatingCarrier: string | null;
    OperatingCarrierName: string | null;
    StopOverTime: string | null;
}
export interface FerrySegment extends Segment {
    DeparturePortCode: string;
    DeparturePortName: string | null;
    DepartureLocationName: string | null;
    ArrivalPortCode: string;
    ArrivalPortName: string | null;
    ArrivalLocationName: string | null;
    FerryName: string | null;
    Company: string | null;
    Vehicle: string | null;
    Vehicles: FerryVehicle[] | null;
    TicketType: FerryTicketType | null;
    Accommodations: FerryAccommodation[];
    OriginalAccommodationsIncludedInPrice: FerryAccommodation[];
    Meals: FerryAddon[];
    Pets: FerryAddon[];
    Lounges: FerryAddon[];
}

export interface FerryTicketType {
    Name: string;
    Description: string;
}

export interface FerryAccommodation extends FerryAddon {
    Type: FerryAccommodationType;
}

export enum FerryAccommodationType {
    Seat,
    Cabin,
}

export interface FerryAddon {
    Title: string;
    Quantity: number;
    Price: Money;
}

export enum CabinClass {
    Unknown,
    Economy,
    Premium,
    Business,
    First,
}

export interface PaymentMethod {
    $type?: string;
    Name: string;
    Code: string;
    Logo?: string | null;
    HasDiscount?: boolean;
    Discount?: Money | null;
    Type: PaymentMethodType;
}

export interface AdyenPaymentMethod extends PaymentMethod {
    BinValue?: string | null;
    CardBrand?: string | null;
    CardLastDigits?: string;
    Issuer?: string;
    Bic?: string;
    EncryptedData?: object;
    BrowserInfo?: object;
    ShopperIP?: string;
    PaymentResponse?: string;
    Key?: string;
}

export type LeaBankPaymentMethod = PaymentMethod;

// DO NOT remove items or change order without doing the same server side, that will cause issues with recognising the type!
export enum PaymentMethodType {
    Adyen,
    PBS,
    Captum,
    MoreBox,
    GiftCard,
    Fake,
    LeaBank,
}

export enum PartPaymentMethodType {
    Unknown,
    FullPayment,
    DownPayment,
}

export interface PartPaymentOption {
    Amount: Money;
    DueDateForNextPayment: string | null;
    Type: PartPaymentMethodType;
    AvailablePaymentOptionTypes: PaymentMethodType[];
}

export type PaymentError = { code?: number; text: string };

export interface TaxOrFee {
    Amount: Money;
    IsIncludedInPrice: boolean;
    IsTax?: boolean;
    Type: string;
}

export interface CancellationPolicy {
    CancellationTime: Date | string;
    Amount: Money;
    FullCost?: boolean;
}

export enum BookingOrigin {
    Checkout,
    Booker,
    Payment,
}

// TODO: Add more Fields, check what is supported in Monolith and checkout v1
export enum MandatoryField {
    Gender = 'Gender',
    FirstName = 'FirstName',
    LastName = 'LastName',
    DateOfBirth = 'DateOfBirth',
    Nationality = 'Nationality',
    PassportExpirationDate = 'PassportExpirationDate',
    PassportNumber = 'PassportNumber',
    Baggage = 'Baggage',
    LoyaltyMembership = 'LoyaltyMembership',
    SpecialNeeds = 'SpecialNeeds',
}

export interface DownPayment {
    Date: string | null;
    Price: {
        Now: Money;
        Remaining: Money;
    };
}

export enum ServicePackageType {
    None = 0,
    Basic = 1,
    Premium = 2,
}
